import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {SignupFormEmailsUpdateInformation, SmartMailingListTooltipMap} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import {ContactsTabSelection} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import type {UserMailingList} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import {smartMailingListsExtraReducers} from '@Components/mailing-list-contacts/mailing-list-contacts-extra-reducer';
import type {SignupForm} from '@/form-maker/grapesjs-form-maker/src/form-maker.types';
import type {SignupFormsUpdateResponse} from '@Components/mailing-list-contacts/mailing-list-contacts-thunk';
import type {RawAudience, UserCustomFieldMeta} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import {ContentOwnershipTypeFilter} from '@Components/content-planner/content-planner.types';

export interface SmartMailingListsState {
  mailingListContentOwnershipFilter: ContentOwnershipTypeFilter;
  signupFormContentOwnershipFilter: ContentOwnershipTypeFilter;
  searchTerm: string | null;
  contactsTabSelection: ContactsTabSelection;
  mailingListSelected: UserMailingList | null;
  userMailingLists: UserMailingList[] | null;
  smartMailingLists: UserMailingList[] | null;
  signupForms: SignupForm[] | null;
  haveUserMailingListsLoaded: boolean;
  signupFormUpdateInformation: SignupFormEmailsUpdateInformation;
  userCustomFieldMeta: UserCustomFieldMeta[];
  shouldLoadMoreSignupForms: boolean;
  shouldLoadMoreMailingLists: boolean;
  hasMoreMailingListsOnServer: boolean;
  hasMoreSignupFormsOnServer: boolean;
  smartMailingListsTooltipMap: SmartMailingListTooltipMap | null;
  shouldShowSidebarActivityIndicator: boolean;
}

const initialState: SmartMailingListsState = {
  mailingListContentOwnershipFilter: ContentOwnershipTypeFilter.ALL,
  signupFormContentOwnershipFilter: ContentOwnershipTypeFilter.ALL,
  searchTerm: null,
  contactsTabSelection: ContactsTabSelection.MAILING_LISTS,
  mailingListSelected: null,
  userMailingLists: null,
  smartMailingLists: null,
  signupForms: null,
  haveUserMailingListsLoaded: false,
  signupFormUpdateInformation: {},
  userCustomFieldMeta: [],
  shouldLoadMoreMailingLists: false,
  shouldLoadMoreSignupForms: false,
  hasMoreMailingListsOnServer: false,
  hasMoreSignupFormsOnServer: false,
  smartMailingListsTooltipMap: null,
  shouldShowSidebarActivityIndicator: false,
};

export const smartMailingListsSlice = createSlice({
  name: 'mailingListContacts',
  initialState,
  reducers: {
    updateSearchTerm: (state, {payload}: PayloadAction<string | null>): void => {
      state.searchTerm = payload;
    },
    updateMailingListContentOwnershipFilter: (state, {payload}: PayloadAction<ContentOwnershipTypeFilter>): void => {
      state.mailingListContentOwnershipFilter = payload;
    },
    updateSignupFormContentOwnershipFilter: (state, {payload}: PayloadAction<ContentOwnershipTypeFilter>): void => {
      state.signupFormContentOwnershipFilter = payload;
    },
    updateContactsTabSelection: (state, {payload}: PayloadAction<ContactsTabSelection>): void => {
      state.contactsTabSelection = payload;
    },
    updateMailingListSelection: (state, {payload}: PayloadAction<UserMailingList | null>): void => {
      state.mailingListSelected = payload;
    },
    updateUserMailingLists: (state, {payload}: PayloadAction<UserMailingList[] | null>): void => {
      state.userMailingLists = payload;
    },
    updateHaveUserMailingListsLoaded: (state, {payload}: PayloadAction<boolean>): void => {
      state.haveUserMailingListsLoaded = payload;
    },
    updateSmartMailingLists: (state, {payload}: PayloadAction<UserMailingList[] | null>): void => {
      state.smartMailingLists = payload;
    },
    updateSignupForms: (state, {payload}: PayloadAction<SignupForm[]>): void => {
      state.signupForms = payload;
    },
    updateSignupFormUpdateInformation: (state, {payload}: PayloadAction<SignupFormEmailsUpdateInformation>): void => {
      state.signupFormUpdateInformation = payload;
    },
    updateShouldLoadMoreMailingLists: (state, {payload}: PayloadAction<boolean>): void => {
      state.shouldLoadMoreMailingLists = payload;
    },
    updateShouldLoadMoreSignupForms: (state, {payload}: PayloadAction<boolean>): void => {
      state.shouldLoadMoreSignupForms = payload;
    },
    resetUserMailingLists: (state): void => {
      state.userMailingLists = null;
      state.hasMoreMailingListsOnServer = false;
    },
    resetSignupForms: (state): void => {
      state.signupForms = null;
      state.hasMoreSignupFormsOnServer = false;
    },
    updateMailingListContacts: (state, {payload}: PayloadAction<MailingListContactUpdatePayload>): void => {
      const {mailingListId, contact} = payload;

      const list = state.userMailingLists?.find((list) => list.id === mailingListId);
      if (!list) {
        return;
      }

      if (!list.mailingListContacts) {
        list.mailingListContacts = [contact];
        return;
      }

      list.mailingListContacts.push(contact);
    },
    updateMailingListName: (state, {payload}: PayloadAction<MailingListNameUpdatePayload>): void => {
      if (state.mailingListSelected && state.mailingListSelected.id === payload.mailingListId) {
        state.mailingListSelected.name = payload.newName;
      }

      if (state.userMailingLists) {
        const mailingList = state.userMailingLists.find((list) => list.id === payload.mailingListId);
        if (mailingList) {
          mailingList.name = payload.newName;
        }
      }
    },
    addMailingList: (state, {payload}: PayloadAction<UserMailingList>): void => {
      if (!state.userMailingLists) {
        state.userMailingLists = [payload];
      } else {
        state.userMailingLists.unshift(payload);
      }
    },
    deleteMailingListWithId: (state, {payload}: PayloadAction<number>): void => {
      if (!state.userMailingLists) {
        return;
      }

      state.userMailingLists = state.userMailingLists.filter((list) => list.id !== payload);
    },
    addSignupForm: (state, {payload}: PayloadAction<SignupForm>): void => {
      if (!state.signupForms) {
        state.signupForms = [payload];
      } else {
        state.signupForms.unshift(payload);
      }

    },
    deleteSignupFormWithId: (state, {payload}: PayloadAction<string>): void => {
      if (!state.signupForms) {
        return;
      }

      state.signupForms = state.signupForms.filter((list) => list.id !== payload);
    },
    unsetNumNewContacts: (state, {payload}: PayloadAction<number>): void => {
      if (!state.userMailingLists) {
        return;
      }

      const mailingList = state.userMailingLists.find((list) => list.id === payload);
      if (mailingList) {
        mailingList.numNewContacts = 0;
      }
    },
    setShouldShowSidebarActivityInformation: (state, {payload}: PayloadAction<boolean>): void => {
      state.shouldShowSidebarActivityIndicator = payload;
    },
  },
  extraReducers: smartMailingListsExtraReducers,
});

export interface MailingListContactUpdatePayload {
  mailingListId: number;
  contact: RawAudience;
}

export interface MailingListNameUpdatePayload {
  mailingListId: number;
  newName: string;
}

export const handleSignupFormUpdate = (state: SmartMailingListsState, updateResponse: SignupFormsUpdateResponse): void => {
  const updateInformation: SignupFormEmailsUpdateInformation = {};

  for (const [formId, newEmails] of Object.entries(updateResponse.signupFormsUpdateMap)) {
    updateInformation[formId] = newEmails;

    const existingForm = state.signupForms?.find((form) => form.id === formId);
    if (existingForm) {
      existingForm.newEmailsCollected = newEmails;
    }
  }

  state.signupFormUpdateInformation = updateInformation;
};

export const {
  updateSearchTerm,
  updateMailingListContentOwnershipFilter,
  updateSignupFormContentOwnershipFilter,
  updateMailingListSelection,
  updateShouldLoadMoreMailingLists,
  updateShouldLoadMoreSignupForms,
  resetUserMailingLists,
  resetSignupForms,
  updateMailingListName,
  addMailingList,
  deleteMailingListWithId,
  addSignupForm,
  deleteSignupFormWithId,
  unsetNumNewContacts,
  setShouldShowSidebarActivityInformation,
} = smartMailingListsSlice.actions;

export const mailingListContactsReducer = smartMailingListsSlice.reducer;
